import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Skeleton } from '@ann/web-components';
import { getAvailableSections } from '../../../../../services/dashboardBuilder';
import { SELECT_LOCATION_COPY } from '../../constants/dashboardBuilderCopy';
import FolderNavigationItem from './FolderNavigationItem';
import NavigationBar from './NavigationBar';
import {
  ModalContent,
  ModalDescription,
  SectionList,
  SkeletonRow,
} from './SelectLocationModal.styled';

const SKELETON_ITEMS = Array.from({ length: 7 });

const buildPathFromSelection = (navigationStack, selectedItem) => {
  const stackPath = navigationStack.map((entry) => entry.name);
  return [...stackPath, selectedItem.name].join(' / ');
};

const SelectLocationModal = ({ open = false, onClose, onSelect }) => {
  const requestIdRef = useRef(0);
  const hasLoadedSectionsRef = useRef(false);
  const selectedItemRef = useRef(null);
  const navigationStackRef = useRef([]);
  const onSelectRef = useRef(onSelect);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [navigationStack, setNavigationStack] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  onSelectRef.current = onSelect;
  selectedItemRef.current = selectedItem;
  navigationStackRef.current = navigationStack;

  const currentItems = useMemo(() => {
    if (!navigationStack.length) {
      return sections;
    }

    return navigationStack[navigationStack.length - 1]?.children || [];
  }, [navigationStack, sections]);
  const isRootLevel = navigationStack.length === 0;
  const breadcrumbPath = useMemo(
    () => navigationStack.map((entry) => entry.name).join(' / '),
    [navigationStack]
  );

  const showSkeleton = loading && sections.length === 0;

  useEffect(() => {
    if (!open) {
      setNavigationStack((prev) => (prev.length ? [] : prev));
      setSelectedItem((prev) => (prev ? null : prev));
      return;
    }

    if (hasLoadedSectionsRef.current) {
      return;
    }

    let isMounted = true;
    const requestId = ++requestIdRef.current;

    const loadSections = async () => {
      setLoading(true);
      try {
        const response = await getAvailableSections();
        if (!isMounted || requestId !== requestIdRef.current) return;
        const nextSections = Array.isArray(response?.body) ? response.body : [];
        setSections(nextSections);
        hasLoadedSectionsRef.current = true;
      } catch (error) {
        console.error('Error fetching sections:', error);
        if (!isMounted || requestId !== requestIdRef.current) return;
        setSections([]);
      } finally {
        if (!isMounted || requestId !== requestIdRef.current) return;
        setLoading(false);
      }
    };

    loadSections();

    return () => {
      isMounted = false;
    };
  }, [open]);

  const handleItemClick = useCallback((item) => {
    if (!item) return;
    const hasChildren = Array.isArray(item.children) && item.children.length > 0;

    if (hasChildren) {
      setNavigationStack((prev) => [...prev, item]);
      setSelectedItem(null);
      return;
    }

    if (!item.supportsDashboard) {
      return;
    }

    setSelectedItem((prev) => (prev?.id === item.id ? null : item));
  }, []);

  const handleConfirm = useCallback(() => {
    const current = selectedItemRef.current;
    if (!current) return;
    onSelectRef.current?.({
      id: current.id,
      path: buildPathFromSelection(navigationStackRef.current, current),
    });
  }, []);
  const handleBack = useCallback(() => {
    setNavigationStack((prev) => prev.slice(0, -1));
    setSelectedItem(null);
  }, []);

  return (
    <Modal isOpen={open} onClose={onClose} size="medium" width="692px">
      <Modal.Header>
        <Modal.Header.Title>{SELECT_LOCATION_COPY.title}</Modal.Header.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalContent>
          <ModalDescription>{SELECT_LOCATION_COPY.description}</ModalDescription>
          {!isRootLevel && (
            <NavigationBar breadcrumb={breadcrumbPath} onBack={handleBack} />
          )}
          <SectionList>
            {showSkeleton
              ? SKELETON_ITEMS.map((_, index) => (
                  <SkeletonRow key={`section-skeleton-${index}`}>
                    <Skeleton height="32px" width="100%" borderRadius="8px" />
                  </SkeletonRow>
                ))
              : currentItems.map((item) => (
                  <FolderNavigationItem
                    key={item.id}
                    item={item}
                    isSelected={selectedItem?.id === item.id}
                    isRootLevel={isRootLevel}
                    onNavigate={handleItemClick}
                  />
                ))}
          </SectionList>
        </ModalContent>
      </Modal.Body>
      <Modal.Actions align="end">
        <Modal.Button
          label={SELECT_LOCATION_COPY.cancelButton}
          type="secondary"
          onClick={onClose}
        />
        <Modal.Button
          label={SELECT_LOCATION_COPY.selectButton}
          type="primary"
          disabled={!selectedItem}
          onClick={handleConfirm}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default memo(SelectLocationModal);
